import { useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import { Download, Upload } from 'lucide-react';
import { Banner, Button, Modal, ModalActions, cx } from '@app/components/ui';

export interface ImportSongData {
  title: string;
  author: string;
  key?: string;
  bpm?: number;
  tempo?: number;
  tags?: string[];
  ccliNumber?: string;
  youtubeUrl?: string;
}

interface ImportSongsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (songs: ImportSongData[]) => void;
}

export const ImportSongsModal: React.FC<ImportSongsModalProps> = ({ isOpen, onClose, onImport }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportSongData[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [downloadAnim, setDownloadAnim] = useState<'json' | 'csv' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setError(null);
    setPreviewData(null);

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (fileExtension !== 'json' && fileExtension !== 'csv') {
      setError('Please upload a JSON or CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        let parsedData: ImportSongData[];

        if (fileExtension === 'json') {
          parsedData = JSON.parse(content);
          if (!Array.isArray(parsedData)) {
            throw new Error('JSON must be an array of song objects');
          }
        } else {
          // Parse CSV
          const result = Papa.parse<any>(content, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header: string) => header.trim().toLowerCase(),
          });

          if (result.errors.length > 0) {
            throw new Error(`CSV parsing error: ${result.errors[0].message}`);
          }

          // Transform CSV data to ImportSongData format
          parsedData = result.data.map((row: any) => {
            const song: ImportSongData = {
              title: row.title || '',
              author: row.author || row.artist || '',
            };

            // Optional fields
            if (row.key) song.key = row.key;
            if (row.bpm) {
              const bpm = parseInt(row.bpm, 10);
              if (!isNaN(bpm) && bpm >= 1 && bpm <= 300) song.bpm = bpm;
            }
            if (row.tempo) {
              const tempo = parseInt(row.tempo, 10);
              if (!isNaN(tempo) && tempo >= 1 && tempo <= 300) song.tempo = tempo;
            }
            if (row.cclinum || row.cclinumber) song.ccliNumber = row.cclinum || row.cclinumber;
            if (row.youtubeurl || row.youtube) song.youtubeUrl = row.youtubeurl || row.youtube;

            return song;
          });
        }

        // Validate required fields
        const invalidRows: number[] = [];
        parsedData.forEach((song, index) => {
          if (!song.title || !song.author) {
            invalidRows.push(index + 1);
          }
        });

        if (invalidRows.length > 0) {
          setError(`Missing title or author in rows: ${invalidRows.slice(0, 5).join(', ')}${invalidRows.length > 5 ? '...' : ''}`);
          return;
        }

        setPreviewData(parsedData);
      } catch (err: any) {
        setError(err.message || 'Failed to parse file');
        setPreviewData(null);
      }
    };

    reader.readAsText(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleImport = () => {
    if (previewData && previewData.length > 0) {
      onImport(previewData);
      handleClose();
    }
  };

  const downloadTemplate = useCallback((format: 'json' | 'csv') => {
    setDownloadAnim(format);
    setTimeout(() => setDownloadAnim(null), 700);

    const sampleSongs: ImportSongData[] = [
      { title: 'Amazing Grace', author: 'John Newton', key: 'G', bpm: 72, ccliNumber: '1234567', youtubeUrl: '' },
      { title: 'How Great Is Our God', author: 'Chris Tomlin', key: 'C', bpm: 78, ccliNumber: '', youtubeUrl: '' },
    ];

    let blob: Blob;
    let filename: string;

    if (format === 'json') {
      blob = new Blob([JSON.stringify(sampleSongs, null, 2)], { type: 'application/json' });
      filename = 'song-import-template.json';
    } else {
      const csv = Papa.unparse(sampleSongs, { header: true });
      blob = new Blob([csv], { type: 'text/csv' });
      filename = 'song-import-template.csv';
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setError(null);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} title="Import songs" subtitle="Bring a CSV or JSON list into the library. Duplicates are skipped.">
      <div className="flex flex-col gap-4">
        <div
          role="button"
          tabIndex={0}
          aria-label="Choose a CSV or JSON file"
          className={cx(
            'rounded-[18px] border-[1.5px] border-dashed p-8 text-center cursor-pointer',
            isDragging ? 'border-acc bg-acc-bg' : error ? 'border-[rgba(208,48,69,.6)] bg-fill' : previewData ? 'border-ok bg-fill' : 'border-line2 bg-fill',
          )}
          style={{ transition: 'background .2s ease, border-color .2s ease' }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <input ref={fileInputRef} type="file" accept=".json,.csv" onChange={handleFileInputChange} className="hidden" />
          <span className="w-12 h-12 rounded-[14px] bg-acc-bg2 text-acc flex items-center justify-center mx-auto mb-3" aria-hidden="true">
            <Upload size={22} strokeWidth={1.8} />
          </span>
          {selectedFile ? (
            <div>
              <p className="text-[14px] font-semibold">{selectedFile.name}</p>
              {previewData && <p className="text-[12.5px] text-ok mt-1">{previewData.length} songs ready to import</p>}
            </div>
          ) : (
            <>
              <p className="text-[14px] font-medium">Drop a CSV or JSON file here</p>
              <p className="text-[12.5px] text-t4 mt-1">or click to choose one from your computer</p>
            </>
          )}
        </div>

        {error && (
          <Banner tone="acc" role="alert">
            {error}
          </Banner>
        )}

        <div className="bg-fill rounded-[14px] p-4 flex flex-col gap-2.5">
          <p className="wp-eyebrow">Download a template</p>
          <p className="text-[13px] text-t3">Fill in your songs, then upload the file above.</p>
          <div className="flex flex-wrap gap-2">
            {(['csv', 'json'] as const).map((f) => (
              <Button
                key={f}
                variant="ghost"
                size="sm"
                onClick={() => downloadTemplate(f)}
                icon={<Download size={14} aria-hidden="true" className={downloadAnim === f ? 'animate-download-bounce' : ''} />}
              >
                {f.toUpperCase()} template
              </Button>
            ))}
          </div>
          <div className="text-[11px] text-t4 font-mono">Fields: title*, author*, key, bpm, ccliNumber, youtubeUrl</div>
        </div>

        <ModalActions
          onCancel={handleClose}
          confirmType="button"
          onConfirm={handleImport}
          disabled={!previewData || previewData.length === 0}
          confirmLabel={`Import${previewData ? ` ${previewData.length} songs` : ''}`}
        />
      </div>
    </Modal>
  );
}
